'use client'

import React, { useCallback, useRef, useState } from 'react'
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'

const MIN_SCALE = 1
const MAX_SCALE = 4
const SCALE_STEP = 0.35

function getTouchDistance(touches: React.TouchList): number {
  if (touches.length < 2) return 0
  const a = touches[0]
  const b = touches[1]
  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)
}

export type ZoomableImageProps = {
  src: string
  alt: string
  /** Container class (e.g. "w-full h-full") */
  className?: string
  /** Optional: show zoom controls overlay (default true) */
  showControls?: boolean
}

/**
 * Zoomable and pannable image for photo viewers.
 * Wheel to zoom, drag to pan when zoomed. Buttons: zoom in, zoom out, reset.
 */
export function ZoomableImage({
  src,
  alt,
  className = 'w-full h-full',
  showControls = true,
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef({ x: 0, y: 0, translateX: 0, translateY: 0 })
  const pinchRef = useRef<{ startDistance: number; startScale: number } | null>(null)

  const clampScale = useCallback((s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s)), [])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale <= 1) return
      e.preventDefault()
      setIsDragging(true)
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        translateX: translate.x,
        translateY: translate.y,
      }
    },
    [scale, translate]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || scale <= 1) return
      const dx = e.clientX - dragStartRef.current.x
      const dy = e.clientY - dragStartRef.current.y
      setTranslate({
        x: dragStartRef.current.translateX + dx,
        y: dragStartRef.current.translateY + dy,
      })
    },
    [isDragging, scale]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  React.useEffect(() => {
    if (!isDragging) return
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Non-passive wheel so we can preventDefault and zoom instead of scroll
  React.useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP
      setScale((prev) => clampScale(prev + delta))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [clampScale])

  // Pinch-to-zoom (and optional two-finger pan when zoomed)
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        pinchRef.current = {
          startDistance: getTouchDistance(e.touches),
          startScale: scale,
        }
      } else if (e.touches.length === 1 && scale > 1) {
        setIsDragging(true)
        dragStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          translateX: translate.x,
          translateY: translate.y,
        }
      }
    },
    [scale, translate]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault()
        const currentDistance = getTouchDistance(e.touches)
        if (currentDistance > 0) {
          const { startDistance, startScale } = pinchRef.current
          const newScale = startScale * (currentDistance / startDistance)
          setScale((prev) => clampScale(newScale))
        }
      } else if (e.touches.length === 1 && isDragging && scale > 1) {
        const dx = e.touches[0].clientX - dragStartRef.current.x
        const dy = e.touches[0].clientY - dragStartRef.current.y
        setTranslate({
          x: dragStartRef.current.translateX + dx,
          y: dragStartRef.current.translateY + dy,
        })
      }
    },
    [clampScale, isDragging, scale]
  )

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      pinchRef.current = null
    }
    if (e.touches.length < 1) {
      setIsDragging(false)
    }
  }, [])

  const zoomIn = useCallback(() => {
    setScale((prev) => clampScale(prev + SCALE_STEP))
  }, [clampScale])

  const zoomOut = useCallback(() => {
    setScale((prev) => clampScale(prev - SCALE_STEP))
  }, [clampScale])

  const resetZoom = useCallback(() => {
    setScale(1)
    setTranslate({ x: 0, y: 0 })
  }, [])

  const canZoomIn = scale < MAX_SCALE
  const canZoomOut = scale > MIN_SCALE
  const isZoomed = scale > 1 || translate.x !== 0 || translate.y !== 0

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ touchAction: 'none' }}
    >
      <div
        className={`absolute inset-0 flex items-center justify-center ${scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: 'center center',
        }}
        onMouseDown={handleMouseDown}
        onDoubleClick={resetZoom}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full object-contain select-none pointer-events-none"
          draggable={false}
          style={{ maxHeight: '100%' }}
        />
      </div>
      {showControls && (
        <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-black/60 p-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={zoomOut}
            disabled={!canZoomOut}
            showLoader={false}
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={zoomIn}
            disabled={!canZoomIn}
            showLoader={false}
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={resetZoom}
            disabled={!isZoomed}
            showLoader={false}
            aria-label="Reset zoom"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
